import { Button } from '@accure/ui/components/button/button-catalyst';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useBoolean } from 'usehooks-ts';

import { Table, TableBody, TableHead, TableRow } from '@/components/ui/table';
import { cls } from '@/utils/cls';
import type { DataResult } from '@/warranty-v3/entities/data-result';
import { orderElements } from '@/warranty-v3/utils/order-elements';

import { columnsRegistry } from './column-registry';

export type OverviewTableProps = {
  unitOverview: DataResult['unitOverview'];
};

export function OverviewTable({ unitOverview }: OverviewTableProps) {
  const showExpandToggle = unitOverview.rows.length > 10;
  const expanded = useBoolean(false);

  const columns = orderElements(unitOverview.columns);
  const rows = unitOverview.rows;

  return (
    <>
      <div
        className={cls('relative w-full', {
          'max-h-[30vh] overflow-y-auto': showExpandToggle && !expanded.value,
        })}
      >
        <Table
          striped
          dense
          bleed
          className="w-full table-auto overflow-x-auto"
        >
          <TableHead className="sticky top-0 bg-gray-100">
            <TableRow>
              {columns.map(function (column) {
                const config = columnsRegistry[column.column];

                if (!config) {
                  return null;
                }

                const Header = config.header;

                return <Header key={column.column} />;
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="py-8 text-center text-gray-500"
                >
                  Nothing to show
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} className="hover:bg-gray-50">
                  {columns.map(function (column) {
                    const config = columnsRegistry[column.column];

                    if (!config) {
                      return null;
                    }

                    const Cell = config.cell;

                    return <Cell key={column.column} row={row} />;
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {/* Fade effect at the bottom when not expanded - sticky positioned */}
        {showExpandToggle && !expanded.value ? (
          <div className="pointer-events-none sticky inset-x-0 bottom-0 -mt-32 h-32 bg-gradient-to-t from-white to-transparent" />
        ) : null}
      </div>

      {/* Expand / Collapse toggle */}
      {showExpandToggle ? (
        <div
          className={cls(
            'flex justify-center text-center',
            expanded.value ? 'mt-3' : 'relative z-10 -mt-8',
          )}
        >
          <Button
            type="button"
            plain
            onClick={expanded.toggle}
            className="flex items-center gap-1.5 rounded-full border border-gray-100 bg-white/80 px-4 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-all duration-150 hover:text-primary-600 hover:shadow"
          >
            {expanded.value ? <ChevronUp /> : <ChevronDown />}
            {expanded.value
              ? 'Show less'
              : `Show all (${unitOverview.rows.length})`}
          </Button>
        </div>
      ) : null}
    </>
  );
}
